import { useEffect, useRef } from 'react';
import { isAndroid, isDesktop } from 'react-device-detect';

const hasModalOpen = (): boolean => {
	return document.querySelectorAll('.modal').length > 0;
};

const resetBodyStyles = () => {
	document.body.style.touchAction = 'auto';
	document.body.style.position = 'static';
	document.body.style.width = 'auto';
};

const resetStyles = () => {
	document.documentElement.style.setProperty('--keyboard-height', '0px');
	document.documentElement.classList.remove('keyboard-open');

	resetBodyStyles();
};

export const useIOSKeyboardLock = () => {
	const pageRef = useRef<HTMLDivElement>(null);
	const isKeyboardOpenRef = useRef(false);

	useEffect(() => {
		if (isDesktop || isAndroid || typeof window === 'undefined') return;

		const visualViewport = window.visualViewport;
		if (!visualViewport) return;

		const initialHeight = window.innerHeight;
		let rafId: number;

		const updateViewport = () => {
			if (hasModalOpen()) {
				resetStyles();
				return;
			}

			const heightDiff = Math.max(0, initialHeight - visualViewport.height);
			const isKeyboardOpen = heightDiff > 150;

			isKeyboardOpenRef.current = isKeyboardOpen;

			document.documentElement.style.setProperty(
				'--keyboard-height',
				isKeyboardOpen ? `${heightDiff}px` : '0px'
			);

			document.documentElement.classList.toggle(
				'keyboard-open',
				isKeyboardOpen
			);

			if (isKeyboardOpen) {
				document.body.style.touchAction = 'none';
				document.body.style.position = 'fixed';
				document.body.style.width = '100%';
			} else {
				resetBodyStyles();
			}
		};

		const handleResize = () => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(updateViewport);
		};

		const preventScroll = (e: TouchEvent) => {
			if (hasModalOpen()) {
				return;
			}

			if (isKeyboardOpenRef.current) {
				const target = e.target as Element;
				const container = pageRef.current;

				if (container && !container.contains(target)) {
					e.preventDefault();
				}
			}
		};

		visualViewport.addEventListener('resize', handleResize, { passive: true });

		document.addEventListener('touchmove', preventScroll, { passive: false });
		document.addEventListener('touchstart', preventScroll, { passive: false });

		return () => {
			visualViewport.removeEventListener('resize', handleResize);
			document.removeEventListener('touchmove', preventScroll);
			document.removeEventListener('touchstart', preventScroll);

			cancelAnimationFrame(rafId);

			resetStyles();
		};
	}, []);

	return pageRef;
};
